import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Loader2, Mail, Phone, BarChart2,
  ChevronDown, ChevronUp, Globe, Check, X, Calendar, Megaphone, Trash2, GripVertical,
} from 'lucide-react';
import { DndContext, closestCenter, DragEndEvent, PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from '@/components/ui/toast';
import { getCountryNames, countryIdByName } from '@/lib/geoApi';
import { TimeDropdown } from '@/components/TimeDropdown';
import { DateInput } from '@/components/ui/DateInput';
import { ConfirmModal } from '@/components/ConfirmModal';
import { formatDate } from '@/lib/dateFormat';
import { SelectField } from '@/components/ui/SelectField';

interface User { id: string; name: string; email: string; phone?: string; }
interface TeamOption { id: string; name: string; description?: string | null; }
interface AssignRule {
  id: string;
  user: { id: string; name: string; email: string; phone?: string };
  teamId?: string | null;
  team?: { id: string; name: string } | null;
  dailyLeadLimit: number;
  monthlyLeadLimit: number;
  weeklyDemoLimit: number;
  includeCountries: string[];
  excludeCountries: string[];
  includeCampaigns?: string[];
  excludeCampaigns?: string[];
  daysOff?: string[]; // yyyy-MM-dd
  workStartTime?: string | null;
  workEndTime?: string | null;
  isActive: boolean;
  currentDailyLeads: number;
  currentMonthlyLeads: number;
  currentWeeklyDemos: number;
}

interface EditState {
  dailyLeadLimit: number;
  monthlyLeadLimit: number;
  weeklyDemoLimit: number;
  includeCountries: string[];
  excludeCountries: string[];
  includeCampaigns: string[];
  excludeCampaigns: string[];
  workStartTime: string;
  workEndTime: string;
  teamId: string;
}

function getInitials(name: string) {
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

function ProgressBar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
      <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
    </div>
  );
}

function CountryList({
  selected,
  mode,
  onToggle,
  countries,
}: {
  selected: string[];
  mode: 'include' | 'exclude';
  onToggle: (c: string) => void;
  countries: string[];
}) {
  const [q, setQ] = useState('');
  const filtered = countries.filter(c => !q || c.toLowerCase().includes(q.toLowerCase()));
  const activeColor = mode === 'include' ? 'text-emerald-700 bg-emerald-50/50 font-medium' : 'text-red-600 bg-red-50/50 font-medium';
  const checkColor = mode === 'include' ? 'text-emerald-500' : 'text-red-400';
  const chipBg = mode === 'include' ? '#E8F5E9' : '#FFEBEE';
  const chipText = mode === 'include' ? '#2E7D32' : '#C62828';

  return (
    <div>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {selected.map(c => (
            <span
              key={c}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
              style={{ backgroundColor: chipBg, color: chipText }}
            >
              {c}
              <button
                type="button"
                onClick={() => onToggle(c)}
                className="ml-0.5 leading-none hover:opacity-70"
                style={{ color: chipText }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="border border-gray-100 rounded-lg overflow-hidden">
        <input
          className="w-full h-8 px-2.5 text-xs border-b border-gray-100 focus:outline-none placeholder:text-slate-300"
          placeholder="Search…"
          value={q}
          onChange={e => setQ(e.target.value)}
        />
        <div className="max-h-36 overflow-y-auto">
          {filtered.map(c => (
            <button
              key={c}
              type="button"
              onClick={() => onToggle(c)}
              className={`flex items-center justify-between gap-2 w-full px-2.5 py-1.5 text-xs text-left break-all hover:bg-gray-50 ${selected.includes(c) ? activeColor : 'text-slate-600'}`}
            >
              {c}
              {selected.includes(c) && <Check className={`h-3 w-3 flex-shrink-0 ${checkColor}`} />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Agent read-only view ─────────────────────────────────────────────────────

function AgentAssignmentView() {
  const { user: me } = useAuth();
  const [rule, setRule] = useState<AssignRule | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/lead-assign-rules')
      .then(({ data }) => {
        const myRule = (data as AssignRule[]).find(r => r.user.id === me?.id);
        setRule(myRule ?? null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [me?.id]);

  if (loading) {
    return (
      <DashboardLayout title="My Assignment">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-[#6366F1]" />
        </div>
      </DashboardLayout>
    );
  }

  if (!rule) {
    return (
      <DashboardLayout title="My Assignment">
        <div className="space-y-5">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">My Assignment</h1>
            <p className="text-sm text-slate-500 mt-0.5">Your lead assignment details</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-6 py-16 text-center text-slate-400 text-sm">
            No assignment configured for your account yet. Contact your admin.
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="My Assignment">
      <div className="space-y-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">My Assignment</h1>
          <p className="text-sm text-slate-500 mt-0.5">Your lead distribution limits and configuration</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          {/* Status */}
          <div className="flex items-center gap-2 mb-6">
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${rule.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
              {rule.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <StatCard label="Daily Leads" value={rule.currentDailyLeads} max={rule.dailyLeadLimit} color="purple" />
            <StatCard label="Monthly Leads" value={rule.currentMonthlyLeads} max={rule.monthlyLeadLimit} color="blue" />
            <StatCard label="Weekly Demos" value={rule.currentWeeklyDemos} max={rule.weeklyDemoLimit} color="amber" />
          </div>

          {/* Working hours */}
          {(rule.workStartTime || rule.workEndTime) && (
            <div className="mb-6 p-4 bg-slate-50 rounded-lg">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Working Hours</p>
              <p className="text-sm text-slate-700">
                {rule.workStartTime || '—'} to {rule.workEndTime || '—'}
              </p>
            </div>
          )}

          {/* Countries */}
          <div className="grid grid-cols-2 gap-4">
            {rule.includeCountries.length > 0 && (
              <div className="p-4 bg-emerald-50/50 rounded-lg border border-emerald-100">
                <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                  <Globe className="h-3.5 w-3.5" /> Included Countries
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {rule.includeCountries.map(c => (
                    <span key={c} className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 text-xs px-2 py-0.5 rounded-md">
                      <Check className="h-3 w-3" />{c}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {rule.excludeCountries.length > 0 && (
              <div className="p-4 bg-red-50/50 rounded-lg border border-red-100">
                <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                  <Globe className="h-3.5 w-3.5" /> Excluded Countries
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {rule.excludeCountries.map(c => (
                    <span key={c} className="inline-flex items-center gap-1 bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded-md">
                      <X className="h-3 w-3" />{c}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {rule.includeCountries.length === 0 && rule.excludeCountries.length === 0 && (
              <div className="col-span-2 p-4 bg-slate-50 rounded-lg text-center">
                <p className="text-sm text-slate-400">All countries — no restrictions</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

// One draggable counselor card. The grip button is the drag handle so the
// rest of the card (toggles, inputs) keeps working normally.
function SortableRow({ id, children }: { id: string; children: (handle: React.ReactNode) => React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const handle = (
    <button
      type="button"
      {...attributes}
      {...listeners}
      title="Drag to change the order leads are assigned in"
      aria-label="Drag to reorder"
      className="flex-shrink-0 cursor-grab touch-none rounded-md p-1 text-slate-300 hover:bg-slate-100 hover:text-slate-500 active:cursor-grabbing"
    >
      <GripVertical className="h-4 w-4" />
    </button>
  );
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 20 : undefined, position: 'relative' }}
      className={isDragging ? 'opacity-90 shadow-xl rounded-xl' : ''}
    >
      {children(handle)}
    </div>
  );
}

function StatCard({ label, value, max, color }: { label: string; value: number; max: number; color: string }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const colorMap: Record<string, { bg: string; bar: string; text: string }> = {
    purple: { bg: 'bg-purple-50', bar: 'bg-purple-500', text: 'text-purple-700' },
    blue: { bg: 'bg-blue-50', bar: 'bg-blue-500', text: 'text-blue-700' },
    amber: { bg: 'bg-amber-50', bar: 'bg-amber-500', text: 'text-amber-700' },
  };
  const c = colorMap[color] ?? colorMap.purple;
  return (
    <div className={`${c.bg} rounded-xl p-4`}>
      <p className="text-xs font-medium text-slate-500 mb-1">{label}</p>
      <p className={`text-2xl font-bold ${c.text}`}>{value}<span className="text-sm font-normal text-slate-400">/{max}</span></p>
      <div className="mt-2 w-full h-1.5 bg-white/60 rounded-full overflow-hidden">
        <div className={`h-full ${c.bar} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ── Admin full view ──────────────────────────────────────────────────────────

export function LeadAssignPage() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === 'admin' || me?.role === 'manager';

  // Agent gets read-only view
  if (!isAdmin) return <AgentAssignmentView />;

  const navigate = useNavigate();
  const [rules, setRules] = useState<AssignRule[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [teams, setTeams] = useState<TeamOption[]>([]);
  const [assignmentMode, setAssignmentMode] = useState<'individual' | 'team' | 'hybrid'>('individual');
  const [countries, setCountries] = useState<string[]>([]);
  const [campaigns, setCampaigns] = useState<string[]>([]);
  // Days-off editor (rule id whose inline row is open) + the date being added.
  const [daysOffOpenId, setDaysOffOpenId] = useState<string | null>(null);
  const [newDayOff, setNewDayOff] = useState('');
  const [savingDaysOffId, setSavingDaysOffId] = useState<string | null>(null);
  const [deleteRule, setDeleteRule] = useState<AssignRule | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [editStates, setEditStates] = useState<Record<string, EditState>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [addingUserId, setAddingUserId] = useState('');
  const [addLoading, setAddLoading] = useState(false);

  const buildEditState = (r: AssignRule): EditState => ({
    dailyLeadLimit: r.dailyLeadLimit,
    monthlyLeadLimit: r.monthlyLeadLimit,
    weeklyDemoLimit: r.weeklyDemoLimit,
    includeCountries: [...r.includeCountries],
    excludeCountries: [...r.excludeCountries],
    includeCampaigns: [...(r.includeCampaigns ?? [])],
    excludeCampaigns: [...(r.excludeCampaigns ?? [])],
    workStartTime: r.workStartTime ?? '',
    workEndTime: r.workEndTime ?? '',
    teamId: r.teamId ?? '',
  });

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/lead-assign-rules');
      setRules(data);
      const states: Record<string, EditState> = {};
      data.forEach((r: AssignRule) => { states[r.id] = buildEditState(r); });
      setEditStates(states);
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to load assign rules');
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRules(); }, [fetchRules]);
  useEffect(() => {
    // Secondary lookups for the assign-rule editor. Failures leave the
    // dropdowns empty — better than spamming three toasts on every page
    // load if /users or /teams briefly returns 5xx.
    api.get('/users')
      .then(({ data }) => setUsers(Array.isArray(data) ? data : data.users ?? data.data ?? []))
      .catch(err => console.warn('Failed to load users:', err?.message));
    api.get('/teams')
      .then(({ data }) => setTeams(data ?? []))
      .catch(err => console.warn('Failed to load teams:', err?.message));
    api.get('/settings').then(({ data }) => {
      const m = data?.leadAssignmentMode as 'individual' | 'team' | 'hybrid' | undefined;
      if (m) setAssignmentMode(m);
    }).catch(err => console.warn('Failed to load settings:', err?.message));
  }, []);
  useEffect(() => {
    // Countries from our own DB geo master (GET /api/geo/countries).
    getCountryNames()
      .then(list => setCountries([...list].sort((a, b) => a.localeCompare(b))))
      .catch(() => {});
    // Distinct campaign values from this workspace's leads (campaign routing).
    api.get('/lead-assign-rules/campaigns')
      .then(({ data }) => setCampaigns(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const toggleActive = async (rule: AssignRule) => {
    setTogglingId(rule.id);
    try {
      await api.patch(`/lead-assign-rules/${rule.id}/toggle`);
      setRules(prev => prev.map(r => r.id === rule.id ? { ...r, isActive: !r.isActive } : r));
    } catch (err: any) { toast('error', err?.response?.data?.message || 'Failed to toggle'); }
    finally { setTogglingId(null); }
  };

  const save = async (rule: AssignRule) => {
    const ed = editStates[rule.id];
    if (!ed) return;
    setSavingId(rule.id);
    try {
      await api.patch(`/lead-assign-rules/${rule.id}`, {
        dailyLeadLimit: ed.dailyLeadLimit,
        monthlyLeadLimit: ed.monthlyLeadLimit,
        weeklyDemoLimit: ed.weeklyDemoLimit,
        includeCountries: ed.includeCountries,
        excludeCountries: ed.excludeCountries,
        // Master ids (names kept above as fallback / display source).
        includeCountryIds: ed.includeCountries.map(c => countryIdByName(c)).filter(Boolean),
        excludeCountryIds: ed.excludeCountries.map(c => countryIdByName(c)).filter(Boolean),
        includeCampaigns: ed.includeCampaigns,
        excludeCampaigns: ed.excludeCampaigns,
        workStartTime: ed.workStartTime || null,
        workEndTime: ed.workEndTime || null,
        teamId: ed.teamId || null,
      });
      toast('success', 'Assignment saved');
      fetchRules();
    } catch (err: any) { toast('error', err?.response?.data?.message || 'Failed to save'); }
    finally { setSavingId(null); }
  };

  // Today (local) as yyyy-MM-dd — past days off are hidden.
  const todayStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();
  const upcomingDaysOff = (r: AssignRule) => (r.daysOff ?? []).filter(d => d >= todayStr).sort();

  // Days off save immediately (independent of "Save Assignment").
  const saveDaysOff = async (rule: AssignRule, next: string[]) => {
    setSavingDaysOffId(rule.id);
    try {
      const { data } = await api.patch(`/lead-assign-rules/${rule.id}`, { daysOff: next });
      setRules(prev => prev.map(r => r.id === rule.id ? { ...r, daysOff: data?.daysOff ?? next } : r));
    } catch (err: any) { toast('error', err?.response?.data?.message || 'Failed to update days off'); }
    finally { setSavingDaysOffId(null); }
  };
  const addDayOff = async (rule: AssignRule) => {
    if (!newDayOff) return;
    if (newDayOff < todayStr) { toast('error', 'Pick today or a future date'); return; }
    // Keep only upcoming dates — past ones are pruned on each change.
    await saveDaysOff(rule, [...new Set([...upcomingDaysOff(rule), newDayOff])].sort());
    setNewDayOff('');
  };
  const removeDayOff = (rule: AssignRule, date: string) =>
    saveDaysOff(rule, upcomingDaysOff(rule).filter(d => d !== date));

  const confirmDeleteRule = async () => {
    if (!deleteRule) return;
    setDeleting(true);
    try {
      await api.delete(`/lead-assign-rules/${deleteRule.id}`);
      toast('success', 'Counselor removed from assignment');
      setDeleteRule(null);
      fetchRules();
    } catch (err: any) { toast('error', err?.response?.data?.message || 'Failed to remove'); }
    finally { setDeleting(false); }
  };

  const addCounselor = async () => {
    if (!addingUserId) return;
    setAddLoading(true);
    try {
      await api.post('/lead-assign-rules', { userId: addingUserId });
      toast('success', 'Counselor added');
      setShowAddDropdown(false);
      setAddingUserId('');
      fetchRules();
    } catch (err: any) { toast('error', err?.response?.data?.message || 'Failed to add'); }
    finally { setAddLoading(false); }
  };

  const toggleExpand = (id: string) =>
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const toggleCountry = (
    ruleId: string,
    field: 'includeCountries' | 'excludeCountries' | 'includeCampaigns' | 'excludeCampaigns',
    country: string,
  ) =>
    setEditStates(prev => {
      const ed = prev[ruleId];
      if (!ed) return prev;
      const arr = ed[field];
      return { ...prev, [ruleId]: { ...ed, [field]: arr.includes(country) ? arr.filter(c => c !== country) : [...arr, country] } };
    });

  const existingUserIds = new Set(rules.map(r => r.user.id));

  // Drag to reorder → this is the round-robin order for new leads.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = async (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const prev = rules;
    const next = arrayMove(rules, rules.findIndex(r => r.id === active.id), rules.findIndex(r => r.id === over.id));
    setRules(next);
    try {
      await api.patch('/lead-assign-rules/order', { ids: next.map(r => r.id) });
      toast('success', 'Assignment order saved');
    } catch {
      setRules(prev);
      toast('error', 'Could not save the order');
    }
  };
  const availableUsers = users.filter(u => !existingUserIds.has(u.id));

  const inputCls = 'h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:border-transparent';

  return (
    <DashboardLayout title="Lead Assign">
      <div className="space-y-5">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Leads Assignment</h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5">Manage lead distribution and limits for sales representatives</p>
          </div>
          <div className="relative flex-shrink-0">
            <button
              onClick={() => setShowAddDropdown(v => !v)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold transition-colors whitespace-nowrap"
            >
              <Plus className="h-4 w-4" /> Add Counselor
            </button>

            {showAddDropdown && (
              <div className="absolute right-0 top-11 z-30 w-72 bg-white rounded-xl shadow-xl border border-gray-100 p-4 space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Select User</p>
                {availableUsers.length === 0 ? (
                  <p className="text-sm text-slate-400">All users are already added.</p>
                ) : (
                  <>
                    <SelectField
                      className={`w-full ${inputCls} bg-white`}
                      value={addingUserId}
                      onChange={e => setAddingUserId(e.target.value)}
                    >
                      <option value="">— Select user</option>
                      {availableUsers.map(u => (
                        <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                      ))}
                    </SelectField>
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => { setShowAddDropdown(false); setAddingUserId(''); }}
                        className="px-3 py-1.5 text-sm text-slate-500 border border-gray-200 rounded-lg hover:border-gray-300"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={addCounselor}
                        disabled={!addingUserId || addLoading}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#6366F1] text-white rounded-lg hover:bg-[#4F46E5] disabled:opacity-60"
                      >
                        {addLoading && <Loader2 className="h-3 w-3 animate-spin" />}
                        Add
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Cards */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-[#6366F1]" />
          </div>
        ) : rules.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-6 py-16 text-center text-slate-400 text-sm">
            No counselors configured. Click "Add Counselor" to get started.
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          {rules.length > 1 && (
            <p className="text-xs text-slate-500">New leads go round-robin in this order — drag <GripVertical className="inline h-3.5 w-3.5 -mt-0.5 text-slate-400" /> to change who gets the next one.</p>
          )}
          <SortableContext items={rules.map(r => r.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-5">
          {rules.map((rule, position) => {
            const ed = editStates[rule.id];
            const isExpanded = expandedIds.has(rule.id);
            const initials = getInitials(rule.user.name);

            return (
              <SortableRow key={rule.id} id={rule.id}>{(handle) => (
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                {/* Card Header — stacks vertically on mobile so identity + stats don't collide */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 px-4 sm:px-5 py-4">
                  {/* Row 1: identity + action icons (right-aligned on mobile so they stay visible) */}
                  <div className="flex items-start justify-between gap-3 min-w-0 lg:flex-1">
                    <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                      <div className="flex items-center gap-1">
                        {handle}
                        <span className="w-5 text-center text-xs font-semibold text-slate-400" title="Position in the round-robin order">{position + 1}</span>
                      </div>
                      <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-[#6366F1]">{initials}</span>
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900 truncate">{rule.user.name}</span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${rule.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                            {rule.isActive ? 'Active' : 'Paused'}
                          </span>
                          {/* Upcoming days off (first 3) + toggle for the inline editor */}
                          {upcomingDaysOff(rule).slice(0, 3).map(d => (
                            <span key={d} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs bg-amber-50 text-amber-700 border border-amber-100">
                              Off {formatDate(d)}
                            </span>
                          ))}
                          {upcomingDaysOff(rule).length > 3 && (
                            <span className="text-[10px] sm:text-xs text-slate-400">+{upcomingDaysOff(rule).length - 3}</span>
                          )}
                          <button
                            type="button"
                            onClick={() => { setDaysOffOpenId(prev => prev === rule.id ? null : rule.id); setNewDayOff(''); }}
                            className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] sm:text-xs text-slate-500 border border-dashed border-slate-300 hover:border-[#6366F1] hover:text-[#6366F1]"
                          >
                            <Plus className="h-3 w-3" /> Days off
                          </button>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4 mt-0.5 text-xs text-slate-400 min-w-0">
                          <span className="flex items-center gap-1 min-w-0">
                            <Mail className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{rule.user.email}</span>
                          </span>
                          {rule.user.phone && (
                            <span className="flex items-center gap-1 flex-shrink-0">
                              <Phone className="h-3 w-3" />{rule.user.phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Mobile-only icon actions — visible on <lg so the header row stays balanced */}
                    <div className="flex items-center gap-1 flex-shrink-0 lg:hidden">
                      <button
                        onClick={() => setDeleteRule(rule)}
                        title="Remove from lead assignment"
                        className="mr-1 p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => toggleActive(rule)}
                        disabled={togglingId === rule.id}
                        className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-60"
                        style={{ backgroundColor: rule.isActive ? '#6366F1' : '#D1D5DB' }}
                      >
                        {togglingId === rule.id
                          ? <Loader2 className="h-3 w-3 animate-spin text-white mx-auto" />
                          : <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${rule.isActive ? 'translate-x-6' : 'translate-x-1'}`} />}
                      </button>
                      <button
                        onClick={() => navigate(`/time-availability?userId=${rule.user.id}`)}
                        title="View time availability"
                        className="p-2 rounded-lg hover:bg-gray-50 text-slate-400 hover:text-slate-600"
                      >
                        <Calendar className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => toggleExpand(rule.id)}
                        className="p-2 rounded-lg hover:bg-gray-50 text-slate-400 hover:text-slate-600"
                      >
                        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Row 2 on mobile / right side on desktop: stats + desktop-only action icons */}
                  <div className="flex items-center justify-between lg:justify-end gap-3 sm:gap-5 flex-shrink-0 pt-3 border-t border-gray-100 lg:pt-0 lg:border-t-0">
                    <div className="flex items-center gap-3 sm:gap-5">
                      <div className="flex flex-col items-start lg:items-end gap-1 min-w-0">
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <BarChart2 className="h-3.5 w-3.5 flex-shrink-0" />
                          <span className="whitespace-nowrap">Leads {rule.currentDailyLeads}/{rule.dailyLeadLimit}</span>
                        </div>
                        <ProgressBar value={rule.currentDailyLeads} max={rule.dailyLeadLimit} />
                      </div>
                      <div className="flex flex-col items-start lg:items-end gap-1 min-w-0">
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <BarChart2 className="h-3.5 w-3.5 flex-shrink-0" />
                          <span className="whitespace-nowrap">Demos {rule.currentWeeklyDemos}/{rule.weeklyDemoLimit}</span>
                        </div>
                        <ProgressBar value={rule.currentWeeklyDemos} max={rule.weeklyDemoLimit} />
                      </div>
                    </div>

                    {/* Desktop-only action icons — on mobile these live in Row 1 */}
                    <div className="hidden lg:flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => setDeleteRule(rule)}
                        title="Remove from lead assignment"
                        className="mr-1 p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => toggleActive(rule)}
                        disabled={togglingId === rule.id}
                        className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-60"
                        style={{ backgroundColor: rule.isActive ? '#6366F1' : '#D1D5DB' }}
                      >
                        {togglingId === rule.id
                          ? <Loader2 className="h-3 w-3 animate-spin text-white mx-auto" />
                          : <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${rule.isActive ? 'translate-x-6' : 'translate-x-1'}`} />}
                      </button>
                      <button
                        onClick={() => navigate(`/time-availability?userId=${rule.user.id}`)}
                        title="View time availability"
                        className="p-2 rounded-lg hover:bg-gray-50 text-slate-400 hover:text-slate-600"
                      >
                        <Calendar className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => toggleExpand(rule.id)}
                        className="p-2 rounded-lg hover:bg-gray-50 text-slate-400 hover:text-slate-600"
                      >
                        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Days off editor — dates with no new leads for this counselor */}
                {daysOffOpenId === rule.id && (
                  <div className="border-t border-gray-100 bg-amber-50/30 px-4 sm:px-5 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs text-slate-600">Days off:</span>
                      {upcomingDaysOff(rule).length === 0 && (
                        <span className="text-xs text-slate-400">None</span>
                      )}
                      {upcomingDaysOff(rule).map(d => (
                        <span key={d} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-50 text-amber-700 border border-amber-100">
                          {formatDate(d)}
                          <button
                            type="button"
                            onClick={() => removeDayOff(rule, d)}
                            disabled={savingDaysOffId === rule.id}
                            className="leading-none hover:opacity-70 disabled:opacity-40"
                            aria-label={`Remove ${formatDate(d)}`}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <div className="w-44">
                        <DateInput value={newDayOff} onChange={setNewDayOff} />
                      </div>
                      <button
                        type="button"
                        onClick={() => addDayOff(rule)}
                        disabled={!newDayOff || savingDaysOffId === rule.id}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-xs font-medium disabled:opacity-50"
                      >
                        {savingDaysOffId === rule.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                        Add day off
                      </button>
                      <span className="text-[11px] text-slate-400">No new leads are assigned to this counselor on these dates.</span>
                    </div>
                  </div>
                )}

                {/* Expanded content */}
                {isExpanded && ed && (
                  <>
                    <div className="border-t border-gray-100 px-5 py-5 grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
                      {/* Column 1: Lead Limit + Demo Limit stacked */}
                      <div className="space-y-5">
                      <div className="border border-gray-100 rounded-xl p-4 space-y-4">
                        <div className="flex items-center gap-2.5">
                          <div className="h-8 w-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                            <BarChart2 className="h-4 w-4 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-800">Lead Limit</p>
                            <p className="text-xs text-slate-400">Daily capacity per representative</p>
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-normal text-slate-600 mb-1.5">Daily Capacity</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number" min={1}
                              className={`w-24 ${inputCls}`}
                              value={ed.dailyLeadLimit}
                              onChange={e => setEditStates(p => ({ ...p, [rule.id]: { ...p[rule.id], dailyLeadLimit: Number(e.target.value) } }))}
                            />
                            <span className="text-xs text-slate-500">Leads</span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-normal text-slate-600 mb-1.5">Monthly Cap</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number" min={1}
                              className={`w-24 ${inputCls}`}
                              value={ed.monthlyLeadLimit}
                              onChange={e => setEditStates(p => ({ ...p, [rule.id]: { ...p[rule.id], monthlyLeadLimit: Number(e.target.value) } }))}
                            />
                            <span className="text-xs text-slate-500">Leads / month</span>
                          </div>
                        </div>

                        <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                          <p className="text-xs text-blue-600">Note: This is the maximum number of leads that can be assigned per day.</p>
                        </div>
                      </div>

                      {/* Demo Limit (under Lead Limit) */}
                      <div className="border border-gray-100 rounded-xl p-4 space-y-4">
                        <div className="flex items-center gap-2.5">
                          <div className="h-8 w-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                            <BarChart2 className="h-4 w-4 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-800">Demo Limit</p>
                            <p className="text-xs text-slate-400">Weekly demonstration quota</p>
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-normal text-slate-600 mb-1.5">Weekly Quota</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number" min={1}
                              className={`w-24 ${inputCls}`}
                              value={ed.weeklyDemoLimit}
                              onChange={e => setEditStates(p => ({ ...p, [rule.id]: { ...p[rule.id], weeklyDemoLimit: Number(e.target.value) } }))}
                            />
                            <span className="text-xs text-slate-500">Demos</span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-normal text-slate-600 mb-1.5">Working Hours</label>
                          {/* Custom TimeDropdown component renders the same
                              "Time (12 Hours)" modal everywhere — previously
                              we used native <input type="time"> which Chrome
                              renders as a scrolling wheel on Windows and a
                              segmented modal on macOS. One look across
                              platforms. */}
                          <div className="grid grid-cols-2 gap-2">
                            <TimeDropdown
                              value={ed.workStartTime}
                              onChange={(v) => setEditStates((p) => ({ ...p, [rule.id]: { ...p[rule.id], workStartTime: v } }))}
                            />
                            <TimeDropdown
                              value={ed.workEndTime}
                              onChange={(v) => setEditStates((p) => ({ ...p, [rule.id]: { ...p[rule.id], workEndTime: v } }))}
                            />
                          </div>
                        </div>

                        <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                          <p className="text-xs text-blue-600">Note: Maximum number of demos that can be scheduled per week.</p>
                        </div>

                        {/* Team scope — only shown when tenant's assignment mode is team or hybrid */}
                        {assignmentMode !== 'individual' && (
                          <div>
                            <label className="block text-xs font-normal text-slate-600 mb-1.5">
                              Team Scope {assignmentMode === 'hybrid' && <span className="text-slate-400 font-normal">(optional)</span>}
                            </label>
                            <SelectField
                              className={`${inputCls} text-xs`}
                              value={ed.teamId}
                              onChange={e => setEditStates(p => ({ ...p, [rule.id]: { ...p[rule.id], teamId: e.target.value } }))}
                            >
                              <option value="">
                                {assignmentMode === 'team' ? 'Select team (required)' : 'None — individual rule'}
                              </option>
                              {teams.map((t) => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                              ))}
                            </SelectField>
                            <p className="text-[11px] text-slate-400 mt-1">
                              {ed.teamId
                                ? 'When this rule matches, round-robin distributes among team members.'
                                : assignmentMode === 'hybrid'
                                  ? 'Leave empty to use rule as an individual fallback.'
                                  : 'Team mode requires a team scope.'}
                            </p>
                          </div>
                        )}
                      </div>

                      </div>

                      {/* Column 2: Regional Routing */}
                      <div className="border border-gray-100 rounded-xl p-4 space-y-4">
                        <div className="flex items-center gap-2.5">
                          <div className="h-8 w-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                            <Globe className="h-4 w-4 text-emerald-600" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-emerald-700">Regional Routing</p>
                            <p className="text-xs text-slate-400">Country-based lead assignment</p>
                          </div>
                        </div>

                        <div>
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <Check className="h-3.5 w-3.5 text-emerald-500" />
                            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Included Countries</span>
                            {ed.includeCountries.length > 0 && (
                              <span className="text-xs text-slate-400">({ed.includeCountries.length})</span>
                            )}
                          </div>
                          <CountryList
                            selected={ed.includeCountries}
                            mode="include"
                            onToggle={c => toggleCountry(rule.id, 'includeCountries', c)}
                            countries={countries}
                          />
                          {ed.includeCountries.length > 0 && (
                            <p className="text-xs text-slate-400 mt-1">Empty = all countries</p>
                          )}
                        </div>

                        <div>
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <X className="h-3.5 w-3.5 text-red-400" />
                            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Excluded Countries</span>
                            {ed.excludeCountries.length > 0 && (
                              <span className="text-xs text-slate-400">({ed.excludeCountries.length})</span>
                            )}
                          </div>
                          <CountryList
                            selected={ed.excludeCountries}
                            mode="exclude"
                            onToggle={c => toggleCountry(rule.id, 'excludeCountries', c)}
                            countries={countries}
                          />
                        </div>
                      </div>

                      {/* Column 3: Campaign Routing */}
                      <div className="border border-gray-100 rounded-xl p-4 space-y-4">
                        <div className="flex items-center gap-2.5">
                          <div className="h-8 w-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                            <Megaphone className="h-4 w-4 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-purple-700">Campaign Routing</p>
                            <p className="text-xs text-slate-400">Campaign-based lead assignment</p>
                          </div>
                        </div>

                        {campaigns.length === 0 ? (
                          <p className="text-xs text-slate-400">No campaigns yet. Campaign names appear here once leads arrive with a campaign (e.g. Facebook Lead Ads or UTM campaign).</p>
                        ) : (
                          <>
                            <div>
                              <div className="flex items-center gap-1.5 mb-1.5">
                                <Check className="h-3.5 w-3.5 text-emerald-500" />
                                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Included Campaigns</span>
                                {ed.includeCampaigns.length > 0 && (
                                  <span className="text-xs text-slate-400">({ed.includeCampaigns.length})</span>
                                )}
                              </div>
                              <CountryList
                                selected={ed.includeCampaigns}
                                mode="include"
                                onToggle={c => toggleCountry(rule.id, 'includeCampaigns', c)}
                                countries={campaigns}
                              />
                              <p className="text-xs text-slate-400 mt-1">Empty = all campaigns</p>
                            </div>

                            <div>
                              <div className="flex items-center gap-1.5 mb-1.5">
                                <X className="h-3.5 w-3.5 text-red-400" />
                                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Excluded Campaigns</span>
                                {ed.excludeCampaigns.length > 0 && (
                                  <span className="text-xs text-slate-400">({ed.excludeCampaigns.length})</span>
                                )}
                              </div>
                              <CountryList
                                selected={ed.excludeCampaigns}
                                mode="exclude"
                                onToggle={c => toggleCountry(rule.id, 'excludeCampaigns', c)}
                                countries={campaigns}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Card Footer */}
                    <div className="flex justify-end px-5 py-3 border-t border-gray-50 bg-gray-50/50">
                      <button
                        onClick={() => save(rule)}
                        disabled={savingId === rule.id}
                        className="flex items-center gap-2 px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-60 transition-colors"
                      >
                        {savingId === rule.id && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        Save Assignment
                      </button>
                    </div>
                  </>
                )}
              </div>
              )}</SortableRow>
            );
          })}
          </div>
          </SortableContext>
          </DndContext>
        )}
      </div>

      <ConfirmModal
        isOpen={!!deleteRule}
        title="Remove counselor?"
        message={`${deleteRule?.user.name ?? 'This counselor'} will stop receiving auto-assigned leads. Their existing leads are not changed.`}
        confirmText="Remove"
        onConfirm={confirmDeleteRule}
        onCancel={() => setDeleteRule(null)}
        isLoading={deleting}
      />
    </DashboardLayout>
  );
}
